import {
  typedDoc,
  getDocument,
  getCollection,
  setDocument,
  updateDocument,
  deleteDocument,
  addDoc,
  orderBy,
  serverTimestamp,
} from '@/lib/firebase/firestore'
import type {
  PressKitConfig,
  PressKitStatDocument,
  PressKitAssetDocument,
  PressKitReleaseDocument,
  PressKitCoverageDocument,
} from '@/types'
import type {
  PressKitConfigFormValues,
  PressKitStatFormValues,
  PressKitReleaseFormValues,
  PressKitCoverageFormValues,
  PressKitStatWithId,
  PressKitAssetWithId,
  PressKitReleaseWithId,
  PressKitCoverageWithId,
} from '@/features/press-kit/types'
import {
  configRef,
  statsCol,
  assetsCol,
  releasesCol,
  coverageCol,
} from '@/features/press-kit/services/pressKit.service'

const statDocRef = (id: string) => typedDoc<PressKitStatDocument>('pressKitStats', id)
const assetDocRef = (id: string) => typedDoc<PressKitAssetDocument>('pressKitAssets', id)
const releaseDocRef = (id: string) => typedDoc<PressKitReleaseDocument>('pressKitReleases', id)
const coverageDocRef = (id: string) => typedDoc<PressKitCoverageDocument>('pressKitCoverage', id)

// ─── Config / Bio ────────────────────────────────────────────────────────────

export const adminPressKitService = {
  async getConfig(): Promise<PressKitConfig | null> {
    return getDocument(configRef())
  },

  async saveConfig(values: PressKitConfigFormValues): Promise<void> {
    await setDocument(configRef(), {
      ...values,
      updatedAt: serverTimestamp(),
    } as unknown as PressKitConfig)
  },

  // ─── Stats ─────────────────────────────────────────────────────────────────

  async getAllStats(): Promise<PressKitStatWithId[]> {
    return getCollection(statsCol(), orderBy('order', 'asc'))
  },

  async createStat(values: PressKitStatFormValues, order: number): Promise<string> {
    const base = {
      label: values.label,
      type: values.type,
      order,
      isVisible: values.isVisible,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }
    const payload =
      values.type === 'manual'
        ? { ...base, value: values.value, derivedKey: undefined }
        : { ...base, value: '', derivedKey: values.derivedKey }

    const ref = await addDoc(statsCol(), payload as unknown as PressKitStatDocument)
    return ref.id
  },

  async updateStat(id: string, values: Partial<PressKitStatFormValues>): Promise<void> {
    await updateDocument(statDocRef(id), {
      ...values,
      updatedAt: serverTimestamp() as unknown as Date,
    })
  },

  async updateStatOrder(id: string, order: number): Promise<void> {
    await updateDocument(statDocRef(id), {
      order,
      updatedAt: serverTimestamp() as unknown as Date,
    })
  },

  async deleteStat(id: string): Promise<void> {
    await deleteDocument(statDocRef(id))
  },

  // ─── Assets ────────────────────────────────────────────────────────────────

  async getAllAssets(): Promise<PressKitAssetWithId[]> {
    return getCollection(assetsCol(), orderBy('order', 'asc'))
  },

  async createAsset(data: Omit<PressKitAssetDocument, 'id' | 'createdAt' | 'updatedAt'>, order: number): Promise<string> {
    const ref = await addDoc(assetsCol(), {
      ...data,
      order,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    } as unknown as PressKitAssetDocument)
    return ref.id
  },

  async updateAsset(id: string, data: Partial<PressKitAssetDocument>): Promise<void> {
    await updateDocument(assetDocRef(id), {
      ...data,
      updatedAt: serverTimestamp() as unknown as Date,
    })
  },

  async updateAssetOrder(id: string, order: number): Promise<void> {
    await updateDocument(assetDocRef(id), {
      order,
      updatedAt: serverTimestamp() as unknown as Date,
    })
  },

  async deleteAsset(id: string): Promise<void> {
    await deleteDocument(assetDocRef(id))
  },

  // ─── Releases ──────────────────────────────────────────────────────────────

  async getAllReleases(): Promise<PressKitReleaseWithId[]> {
    return getCollection(releasesCol(), orderBy('publishedAt', 'desc'))
  },

  async createRelease(values: PressKitReleaseFormValues): Promise<string> {
    const slug = values.title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-')

    const ref = await addDoc(releasesCol(), {
      title: values.title,
      slug,
      content: values.content,
      summary: values.summary,
      publishedAt: values.publishedAt,
      isPublished: values.isPublished,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    } as unknown as PressKitReleaseDocument)
    return ref.id
  },

  async updateRelease(id: string, values: Partial<PressKitReleaseFormValues>): Promise<void> {
    const updates: Record<string, unknown> = {
      ...values,
      updatedAt: serverTimestamp(),
    }
    if (values.title) {
      updates.slug = values.title
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-')
    }
    await updateDocument(releaseDocRef(id), updates as unknown as Partial<PressKitReleaseDocument>)
  },

  async deleteRelease(id: string): Promise<void> {
    await deleteDocument(releaseDocRef(id))
  },

  // ─── Coverage ──────────────────────────────────────────────────────────────

  async getAllCoverage(): Promise<PressKitCoverageWithId[]> {
    return getCollection(coverageCol(), orderBy('order', 'asc'))
  },

  async createCoverage(values: PressKitCoverageFormValues, order: number): Promise<string> {
    const ref = await addDoc(coverageCol(), {
      ...values,
      thumbnailUrl: '',
      order,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    } as unknown as PressKitCoverageDocument)
    return ref.id
  },

  async updateCoverage(id: string, values: Partial<PressKitCoverageFormValues>): Promise<void> {
    await updateDocument(coverageDocRef(id), {
      ...values,
      updatedAt: serverTimestamp() as unknown as Date,
    })
  },

  async updateCoverageThumbnail(id: string, thumbnailUrl: string): Promise<void> {
    await updateDocument(coverageDocRef(id), {
      thumbnailUrl,
      updatedAt: serverTimestamp() as unknown as Date,
    })
  },

  async updateCoverageOrder(id: string, order: number): Promise<void> {
    await updateDocument(coverageDocRef(id), {
      order,
      updatedAt: serverTimestamp() as unknown as Date,
    })
  },

  async deleteCoverage(id: string): Promise<void> {
    await deleteDocument(coverageDocRef(id))
  },
}
